import { createApp } from "@vue/runtime-dom";
import { createPinia } from "pinia";
import App from "./App.vue";
import router from "./router";
import "./assets/main.css";
import * as Sentry from "@sentry/vue";
// Import Font Awesome
import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { fas } from "@fortawesome/free-solid-svg-icons";
import Toast from "vue-toastification";
import "vue-toastification/dist/index.css";
// Import des optimisations de performance
import PerformancePlugin from "./plugins/performance";
import InitPlugin from "./plugins/init";
import "vue-virtual-scroller/dist/vue-virtual-scroller.css";

// Fonction pour détecter Windows 7
function isWindows7(): boolean {
  const ua = navigator.userAgent;
  return ua.indexOf('Windows NT 6.1') !== -1;
}

// Fonction pour obtenir le message d'erreur adapté selon l'OS
function getCompatibilityMessage(): string {
  if (isWindows7()) {
    return `Votre système (Windows 7) n'est plus supporté par les navigateurs modernes.<br><br>
            <strong>Solutions recommandées:</strong><br>
            • <strong>Option 1 (Recommandée):</strong> Mettre à jour vers Windows 10 ou 11<br>
            • <strong>Option 2:</strong> Utiliser Firefox ESR 115 (dernière version compatible)<br>
            • <strong>Option 3:</strong> Vider le cache du navigateur (Ctrl+Shift+Suppr)<br><br>
            Contactez le support technique si vous avez besoin d'aide.`;
  } else {
    return `Votre navigateur semble incompatible avec cette version de l'application.<br><br>
            <strong>Recommandations:</strong><br>
            • Mettez à jour votre navigateur vers la dernière version<br>
            • Utilisez un navigateur moderne (Chrome, Firefox, Edge)<br>
            • Videz le cache de votre navigateur (Ctrl+Shift+Suppr)<br><br>
            Si le problème persiste, contactez le support technique.`;
  }
}

// Gestionnaire d'erreur global pour les erreurs de chargement de modules dynamiques
// Ceci corrige les erreurs qui surviennent après un déploiement avec des navigateurs anciens
window.addEventListener('error', function(event) {
  // Détecter les erreurs de chargement de modules (chunk loading)
  if (event.message && (
    event.message.includes('dynamically imported module') ||
    event.message.includes('Failed to fetch dynamically imported module') ||
    event.message.includes('error loading dynamically imported module') ||
    event.message.includes('Loading chunk') ||
    event.message.includes('ChunkLoadError')
  )) {
    event.preventDefault();

    // Vérifier si on a déjà rechargé pour éviter une boucle infinie
    const hasReloaded = sessionStorage.getItem('page-has-been-force-reloaded');

    if (!hasReloaded) {
      console.warn('Erreur de chargement de module détectée, rechargement de la page...');
      console.warn('Système détecté:', isWindows7() ? 'Windows 7' : 'Autre');
      console.warn('User Agent:', navigator.userAgent);
      sessionStorage.setItem('page-has-been-force-reloaded', 'true');

      // Afficher un message à l'utilisateur avant de recharger
      alert('Une nouvelle version de l\'application est disponible. La page va se recharger automatiquement.');

      // Forcer le rechargement depuis le serveur (hard reload)
      window.location.reload();
    } else {
      // Si on a déjà rechargé une fois, afficher un message d'erreur plus détaillé
      console.error('Erreur persistante après rechargement:', event.message);
      console.error('User Agent:', navigator.userAgent);
      console.error('Windows 7 détecté:', isWindows7());

      // Afficher le message d'erreur adapté
      const messageHtml = getCompatibilityMessage();
      const errorDiv = document.createElement('div');
      errorDiv.innerHTML = `
        <div style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 99999; display: flex; align-items: center; justify-content: center; padding: 20px;">
          <div style="background: white; padding: 30px; border-radius: 10px; max-width: 600px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);">
            <h2 style="color: #B70018; margin-top: 0; font-size: 24px; font-weight: bold;">Erreur de compatibilité</h2>
            <div style="margin: 20px 0; line-height: 1.6; color: #333;">${messageHtml}</div>
            <button onclick="this.parentElement.parentElement.remove(); sessionStorage.removeItem('page-has-been-force-reloaded');"
                    style="background: #B70018; color: white; border: none; padding: 12px 24px; border-radius: 5px; cursor: pointer; font-size: 16px; font-weight: 500;">
              Compris
            </button>
          </div>
        </div>
      `;
      document.body.appendChild(errorDiv);

      // Nettoyer le flag pour permettre un nouveau rechargement si l'utilisateur réessaie
      // (fait par le bouton)
    }
  }
}, true);

// Nettoyer le flag de rechargement quand la page se charge correctement
window.addEventListener('load', function() {
  // Attendre un peu avant de nettoyer pour s'assurer que tout est chargé
  setTimeout(function() {
    sessionStorage.removeItem('page-has-been-force-reloaded');
  }, 1000);
});

// Enregistrement du service worker pour la gestion du cache
// TEMPORAIREMENT DÉSACTIVÉ pour debug
if ("serviceWorker" in navigator && import.meta.env.PROD && false) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then((_registration) => {
        console.log("Service Worker enregistré avec succès");
      })
      .catch((error) => {
        console.error("Échec de l'enregistrement du Service Worker:", error);
      });
  });
}

// Désenregistrer les anciens service workers
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((registration) => {
      registration.unregister();
      console.log("Service Worker désenregistré");
    });
  });
}

// Add all icons to the library
library.add(fas);

const app = createApp(App);
const pinia = createPinia();
Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
  sampleRate: import.meta.env.VITE_SENTRY_SAMPLE_RATE,
});
app.component("FontAwesomeIcon", FontAwesomeIcon);

app.use(pinia);
app.use(router);
app.use(InitPlugin); // Plugin d'initialisation - rafraîchit les données au démarrage
app.use(PerformancePlugin); // Plugin d'optimisation de performance
app.use(Toast, {
  position: "top-right",
  timeout: 2000,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  pauseOnHover: true,
  draggable: true,
  draggablePercent: 0.6,
  showCloseButtonOnHover: false,
  hideProgressBar: false,
  closeButton: "button",
  icon: true,
  rtl: false,
});

app.mount("#app");
